import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SEOHead from "@/components/feature/SEOHead";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ComicCard from "@/components/base/ComicCard";
import { comics } from "@/mocks/comics";
import { categories } from "@/mocks/categories";

const ITEMS_PER_PAGE = 8;

export default function Category() {
  const { slug = "all" } = useParams<{ slug: string }>();
  const [currentPage, setCurrentPage] = useState(1);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];

  const filteredComics =
    slug === "all"
      ? comics
      : comics.filter((c) => c.categorySlug === slug);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const paginatedComics = filteredComics.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleCategoryChange = (newSlug: string) => {
    setCurrentPage(1);
  };

  const pageTitle =
    slug === "all"
      ? "漫画大全 - 韩漫漫画网"
      : `${currentCategory.name}韩漫 - 韩漫漫画网`;
  const pageDesc =
    slug === "all"
      ? "韩漫漫画网漫画大全，收录海量韩国漫画，涵盖恋爱、奇幻、动作、悬疑、搞笑、耽美等多种类型，全部免费在线观看。"
      : `韩漫漫画网${currentCategory.name}分类，提供${currentCategory.count}部${currentCategory.name}类韩漫完整在线观看，每日更新，免费阅读。`;
  const pageKeywords =
    slug === "all"
      ? "漫画大全,韩漫漫画网,韩漫完整在线观看,免费韩漫"
      : `${currentCategory.name}韩漫,${currentCategory.name}漫画,韩漫漫画网,韩漫在线观看`;

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: pageTitle,
    description: pageDesc,
    url: `https://www.hanmancomic.com/category/${slug}`,
  };

  return (
    <Layout>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`https://www.hanmancomic.com/category/${slug}`}
        schemaJson={[webPageSchema]}
      />

      <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-6">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            {
              label: slug === "all" ? "漫画大全" : currentCategory.name,
            },
          ]}
        />

        <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
          {slug === "all" ? "漫画大全" : `${currentCategory.name}韩漫`}
        </h2>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category/${cat.slug}`}
              onClick={() => handleCategoryChange(cat.slug)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                slug === cat.slug
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {cat.name}
              <span className="ml-1 text-xs opacity-70">({cat.count})</span>
            </Link>
          ))}
        </div>

        {paginatedComics.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4 mb-8">
              {paginatedComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>

            {totalPages > 1 && (
              <nav className="flex items-center justify-center gap-2" aria-label="分页导航">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  aria-label="上一页"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                      currentPage === page
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-200/70 bg-background-50 text-foreground-600 hover:bg-background-100"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  aria-label="下一页"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </nav>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <p className="text-foreground-500 text-sm">该分类暂无漫画内容</p>
            <Link
              to="/category/all"
              className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              查看全部漫画
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}